import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  AlertTriangle,
  BadgeCheck,
  BellOff,
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  CircleDollarSign,
  CreditCard,
  FilterX,
  History,
  Mail,
  MailOpen,
  MailX,
  MousePointerClick,
  PauseCircle,
  X,
} from 'lucide-react';

import { cn } from '../../../lib/utils';
import dayjs from '../../../lib/day';
import { money } from '../shared/billingFormat';
import { useFetchBillingSchedule } from '../../../services/billingAdmin';

// Right-hand slide-over with two modes:
// - Upcoming: the agenda of what the billing engine is SET to do over the
//   next 30 days — a read-only projection from the live cadence settings +
//   real installment rows (see getBillingSchedule). Opening it can never
//   trigger a send.
// - History: the REAL record of what actually happened — emails sent (with
//   engagement), installment charges that succeeded or failed, and invoices
//   paid. One 30-day window at a time, pageable back to 90 days.

const HISTORY_WINDOW_DAYS = 30;
const HISTORY_MAX_PAGE = 2; // 3 windows x 30 days = the server's 90-day cap

const EVENT_STYLES = {
  installment: {
    icon: CircleDollarSign,
    chip: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    label: 'Installment charge',
  },
  planOffer: {
    icon: CreditCard,
    chip: 'bg-violet-500/10 text-violet-600 dark:text-violet-400',
    label: 'Plan offer email',
  },
  reminder: {
    icon: Mail,
    chip: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
    label: 'Reminder email',
  },
};

// History variants: SAME color language as Upcoming (Nic's call — flipping
// tabs must not re-teach the palette); only the labels/icons say "this
// already happened". Payments are history-only and get their own teal.
const EVENT_STYLES_HISTORY = {
  reminder: {
    icon: Mail,
    chip: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
    label: 'Reminder sent',
  },
  initial: {
    icon: Mail,
    chip: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
    label: 'Invoice email sent',
  },
  planOffer: {
    icon: CreditCard,
    chip: 'bg-violet-500/10 text-violet-600 dark:text-violet-400',
    label: 'Plan offer email sent',
  },
  installment: {
    icon: CircleDollarSign,
    chip: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    label: 'Installment charged',
  },
  payment: {
    // Same emerald family as installment charges — history folds all money
    // events under the one Charges chip; the BadgeCheck icon marks a full
    // invoice payment vs an installment.
    icon: BadgeCheck,
    chip: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    label: 'Invoice paid',
  },
};

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// Why a held row will not send/charge (event.meta.mutedReason).
const MUTED_REASON_COPY = {
  dispute_hold: 'dispute hold',
  paused: 'reminders paused',
  suppressed: 'excluded list',
};

const eventDetail = (event) => {
  const meta = event.meta || {};
  if (event.type === 'reminder') {
    if (event.historical && meta.manual) return 'Manual reminder email';
    const stepLabel =
      meta.step <= 0 ? 'due-today email' : `day ${meta.step} past due`;
    if (event.historical) return `Reminder, ${stepLabel}`;
    return meta.catchup
      ? `Reminder catch-up (${stepLabel})`
      : `Reminder, ${stepLabel}`;
  }
  if (event.type === 'initial') {
    return 'Invoice email';
  }
  if (event.type === 'planOffer') {
    if (event.historical) return 'Payment plan offer email';
    return meta.catchup
      ? 'Payment plan offer (catching up)'
      : 'Payment plan offer opens';
  }
  if (event.type === 'installment') {
    const which =
      meta.sequence === 0
        ? 'Down payment'
        : `Installment ${meta.number} of ${meta.totalCount}`;
    // History keeps it lean: the outcome lives in the amount's color, not
    // crammed into this line, and the card detail is upcoming-only.
    if (event.historical) return which;
    const card = meta.cardBrand
      ? `, ${cap(meta.cardBrand)} ····${meta.cardLast4}`
      : '';
    return `${which}${card}`;
  }
  if (event.type === 'payment') {
    return 'Invoice paid in full';
  }
  return '';
};

const dayTitle = (date) => {
  const d = dayjs(date);
  const today = dayjs().startOf('day');
  if (d.isSame(today, 'day')) return 'Today';
  if (d.isSame(today.add(1, 'day'), 'day')) return 'Tomorrow';
  if (d.isSame(today.subtract(1, 'day'), 'day')) return 'Yesterday';
  return d.format('ddd, MMM D');
};

const heatClass = (count) => {
  if (count === 0) return 'bg-muted/40 text-muted-foreground/60';
  if (count <= 2) return 'bg-primary/15 text-foreground';
  if (count <= 5) return 'bg-primary/30 text-foreground';
  return 'bg-primary/50 text-foreground';
};

// Sepia palette so a glance says "this is the past".
const heatClassHistory = (count) => {
  if (count === 0) return 'bg-stone-100/60 text-muted-foreground/50';
  if (count <= 2) return 'bg-amber-200/40 text-stone-700';
  if (count <= 5) return 'bg-amber-300/50 text-stone-800';
  return 'bg-amber-400/50 text-stone-900';
};

// Engagement outcome for a historical email event: the best signal wins.
const EmailOutcome = ({ meta }) => {
  if (meta?.bouncedAt) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-status-error-fg">
        <MailX className="h-3.5 w-3.5" /> Bounced
      </span>
    );
  }
  if (meta?.clickedAt) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-status-success-fg">
        <MousePointerClick className="h-3.5 w-3.5" /> Clicked
      </span>
    );
  }
  if (meta?.openedAt) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-status-info-fg">
        <MailOpen className="h-3.5 w-3.5" /> Opened
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
      <Mail className="h-3.5 w-3.5" /> Not opened
    </span>
  );
};

const EMAIL_TYPES = new Set(['reminder', 'planOffer', 'initial']);

const SkeletonRows = () => (
  <div className="space-y-6 px-5 pt-4">
    {[0, 1, 2].map((section) => (
      <div key={section} className="space-y-3">
        <div className="h-3 w-24 animate-pulse rounded bg-muted" />
        {[0, 1].map((row) => (
          <div key={row} className="flex items-center gap-3">
            <div className="h-9 w-9 animate-pulse rounded-lg bg-muted" />
            <div className="flex-1 space-y-1.5">
              <div className="h-3 w-32 animate-pulse rounded bg-muted" />
              <div className="h-2.5 w-44 animate-pulse rounded bg-muted" />
            </div>
            <div className="h-3 w-14 animate-pulse rounded bg-muted" />
          </div>
        ))}
      </div>
    ))}
  </div>
);

const BillingSchedulePanel = ({ onClose }) => {
  const [shown, setShown] = useState(false);
  const dayRefs = useRef({});

  // Mode + history paging: page 0 = the last 30 days, each earlier page one
  // 30-day window further back (server caps the look-back at 90 days).
  const [mode, setMode] = useState('upcoming'); // 'upcoming' | 'history'
  const [historyPage, setHistoryPage] = useState(0);
  const historyStart = useMemo(
    () =>
      dayjs()
        .startOf('day')
        .subtract(
          HISTORY_WINDOW_DAYS - 1 + historyPage * HISTORY_WINDOW_DAYS,
          'day',
        )
        .format('YYYY-MM-DD'),
    [historyPage],
  );
  const queryParams = useMemo(
    () =>
      mode === 'history'
        ? { days: HISTORY_WINDOW_DAYS, start: historyStart }
        : { days: 30 },
    [mode, historyStart],
  );
  const { data, isLoading, isError, isFetching } =
    useFetchBillingSchedule(queryParams);
  const historical = Boolean(data?.historical);

  // Two-frame mount so the panel actually animates in from off-screen.
  useEffect(() => {
    const raf = requestAnimationFrame(() =>
      requestAnimationFrame(() => setShown(true)),
    );
    return () => cancelAnimationFrame(raf);
  }, []);

  const close = useCallback(() => {
    setShown(false);
    setTimeout(onClose, 250);
  }, [onClose]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && close();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [close]);

  // Lock page scroll while the panel is open. Hiding the scrollbar widens
  // the viewport and shifts the page; padding the body by the scrollbar's
  // width keeps the layout perfectly still.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    const prevPadding = document.body.style.paddingRight;
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPadding;
    };
  }, []);

  const days = useMemo(() => data?.days || [], [data]);

  // Type filters: hide/show event categories. Pure client-side — the heat
  // strip, agenda, and day totals all recompute from whatever is visible.
  const [hiddenTypes, setHiddenTypes] = useState(() => new Set());
  const toggleTypes = (types) => {
    setHiddenTypes((prev) => {
      const next = new Set(prev);
      const anyVisible = types.some((t) => !next.has(t));
      types.forEach((t) => {
        if (anyVisible) next.add(t);
        else next.delete(t);
      });
      return next;
    });
  };

  // Person focus: clicking a row narrows the agenda to that client; while
  // focused, clicking any row clears it (only their rows are visible anyway).
  const [focusUser, setFocusUser] = useState(null); // { userId, client }
  const toggleFocus = (event) => {
    setFocusUser((prev) =>
      prev ? null : { userId: event.userId, client: event.client },
    );
  };

  const switchMode = (next) => {
    if (next === mode) return;
    setMode(next);
    setHistoryPage(0);
    // A person focused in one mode may not exist in the other.
    setFocusUser(null);
  };

  const visibleDays = useMemo(() => {
    const mapped = days
      .map((day) => {
        const events = day.events.filter(
          (e) =>
            !hiddenTypes.has(e.type) &&
            (!focusUser || e.userId === focusUser.userId),
        );
        const live = events.filter((e) => !e.muted);
        return {
          ...day,
          events,
          count: live.length,
          totalAmount:
            Math.round(
              live.reduce((sum, e) => sum + Number(e.amount || 0), 0) * 100,
            ) / 100,
        };
      })
      .filter((day) => day.events.length > 0);
    // History reads like a log: most recent day first.
    return historical ? [...mapped].reverse() : mapped;
  }, [days, hiddenTypes, focusUser, historical]);

  // Calendar-aligned heat strip: a single weekday header row plus
  // leading-blank cells pad the window's first day to its weekday, so the
  // columns are true weekday columns (30 days spans 5-6 rows). Intensity
  // counts LIVE events only — a day holding nothing but muted rows stays
  // cool.
  const heatCells = useMemo(() => {
    const byDate = new Map(visibleDays.map((d) => [d.date, d]));
    const todayKey = dayjs().startOf('day').format('YYYY-MM-DD');
    const start =
      historical && data?.windowStart
        ? dayjs(data.windowStart)
        : dayjs().startOf('day');
    const horizon = data?.horizonDays || 30;
    const cells = Array.from({ length: horizon }, (_, i) => {
      const d = start.add(i, 'day');
      const key = d.format('YYYY-MM-DD');
      const day = byDate.get(key);
      return {
        key,
        blank: false,
        dayNum: d.date(),
        count: day?.count || 0,
        hasAny: (day?.events?.length || 0) > 0,
        totalAmount: day?.totalAmount || 0,
        isToday: key === todayKey,
      };
    });
    const blanks = Array.from({ length: start.day() }, (_, i) => ({
      key: `blank-${i}`,
      blank: true,
    }));
    return [...blanks, ...cells];
  }, [visibleDays, data, historical]);

  const scrollToDay = (key) => {
    dayRefs.current[key]?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const summary = data?.summary;

  // Type filter chips per mode. Each chip governs one or more event types
  // (history groups all three email flavors under one "Emails" chip).
  // The SAME three chips (labels + colors) in both modes — flipping tabs
  // must not re-teach the vocabulary. History folds initial invoice emails
  // into Reminders, and full invoice payments into Charges (all money
  // events); per-row, the amount's color tells the outcome.
  const typeChips = [
    {
      key: 'reminder',
      types: historical ? ['reminder', 'initial'] : ['reminder'],
      label: 'Reminders',
      count: historical
        ? (summary?.reminders || 0) + (summary?.initials || 0)
        : summary?.reminders || 0,
      icon: Mail,
      on: 'border-transparent bg-blue-500/10 text-blue-700 dark:text-blue-400',
    },
    {
      key: 'planOffer',
      types: ['planOffer'],
      label: 'Plan offers',
      count: summary?.planOffers || 0,
      icon: CreditCard,
      on: 'border-transparent bg-violet-500/10 text-violet-700 dark:text-violet-400',
    },
    {
      key: 'installment',
      types: historical ? ['installment', 'payment'] : ['installment'],
      label: 'Charges',
      count: historical
        ? (summary?.installments || 0) + (summary?.payments || 0)
        : summary?.installments || 0,
      icon: CircleDollarSign,
      on: 'border-transparent bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
    },
  ];

  // Portal to <body>: rendered in place, the fixed wrapper is a child of the
  // page's space-y-6 stack, whose `> * + *` margin-top shifts the whole
  // overlay down and leaves an un-dimmed strip above the backdrop.
  return createPortal(
    <div className="fixed inset-0 z-[1060]" role="dialog" aria-modal="true">
      <div
        className={cn(
          'absolute inset-0 bg-black/40 backdrop-blur-[2px] transition-opacity duration-300',
          shown ? 'opacity-100' : 'opacity-0',
        )}
        onClick={close}
        aria-hidden="true"
      />
      <div
        className={cn(
          'absolute inset-y-0 right-0 flex w-full max-w-md flex-col border-l border-border bg-background shadow-2xl transition-transform duration-300 ease-out sm:rounded-l-2xl',
          shown ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-border px-5 py-4">
          <div
            className={cn(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
              historical
                ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400'
                : 'bg-violet-500/10 text-violet-600 dark:text-violet-400',
            )}
          >
            {historical ? (
              <History className="h-5 w-5" />
            ) : (
              <CalendarClock className="h-5 w-5" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="mb-0 truncate text-base font-semibold text-foreground">
              Billing Schedule
            </h2>
            <p className="mb-0 truncate text-xs text-muted-foreground">
              {mode === 'history'
                ? 'What actually happened'
                : `Projected sends and charges, next ${data?.horizonDays || 30} days`}
            </p>
          </div>
          <button
            type="button"
            onClick={close}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Mode toggle */}
        <div className="flex justify-center border-b border-border px-5 py-2.5">
          <div className="inline-flex overflow-hidden rounded-lg border border-border">
            <button
              type="button"
              onClick={() => switchMode('history')}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors',
                mode === 'history'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-card text-muted-foreground hover:bg-accent hover:text-foreground',
              )}
            >
              <History className="h-3.5 w-3.5" />
              History
            </button>
            <button
              type="button"
              onClick={() => switchMode('upcoming')}
              className={cn(
                'flex items-center gap-1.5 border-l border-border px-3 py-1.5 text-xs font-medium transition-colors',
                mode === 'upcoming'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-card text-muted-foreground hover:bg-accent hover:text-foreground',
              )}
            >
              <CalendarClock className="h-3.5 w-3.5" />
              Upcoming
            </button>
          </div>
        </div>

        {mode === 'history' ? (
          <div className="flex items-center gap-2 border-b border-amber-500/30 bg-amber-500/10 px-5 py-2 text-xs text-amber-800 dark:text-amber-400">
            <History className="h-3.5 w-3.5 shrink-0" />
            <span className="flex-1">
              Viewing history
              {data?.windowStart
                ? `: ${dayjs(data.windowStart).format('MMM D')} to ${dayjs(data.windowEnd).format('MMM D')}`
                : ''}
            </span>
            <span className="flex shrink-0 items-center gap-1">
              <button
                type="button"
                onClick={() =>
                  setHistoryPage((p) => Math.min(HISTORY_MAX_PAGE, p + 1))
                }
                disabled={historyPage >= HISTORY_MAX_PAGE || isFetching}
                className="rounded-md p-1 transition-colors hover:bg-amber-500/15 disabled:opacity-40"
                aria-label="Earlier window"
                title="Earlier 30 days"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => setHistoryPage((p) => Math.max(0, p - 1))}
                disabled={historyPage === 0 || isFetching}
                className="rounded-md p-1 transition-colors hover:bg-amber-500/15 disabled:opacity-40"
                aria-label="Later window"
                title="Later 30 days"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </span>
          </div>
        ) : null}

        {mode === 'upcoming' && data && data.remindersEnabled === false ? (
          <div className="flex items-center gap-2 border-b border-amber-500/30 bg-amber-500/10 px-5 py-2 text-xs text-amber-700 dark:text-amber-400">
            <PauseCircle className="h-3.5 w-3.5 shrink-0" />
            Reminder engine is paused. Reminders and plan offers shown here are
            on hold; installment charges still run.
          </div>
        ) : null}

        {/* Heat strip (calendar-aligned) */}
        {!isLoading && !isError ? (
          <div className="border-b border-border px-5 py-3">
            <div className="mb-1 grid grid-cols-7 gap-1.5">
              {'SMTWTFS'.split('').map((letter, i) => (
                <div
                  // eslint-disable-next-line react/no-array-index-key
                  key={`wd-${i}`}
                  className="text-center text-[10px] font-medium text-muted-foreground/60"
                >
                  {letter}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1.5">
              {heatCells.map((cell) =>
                cell.blank ? (
                  <div key={cell.key} />
                ) : (
                  <button
                    key={cell.key}
                    type="button"
                    onClick={() => cell.hasAny && scrollToDay(cell.key)}
                    title={
                      cell.count > 0
                        ? `${cell.count} event${cell.count === 1 ? '' : 's'}, ${money(cell.totalAmount)}`
                        : cell.hasAny
                          ? 'Held items only'
                          : historical
                            ? 'Nothing happened'
                            : 'Nothing scheduled'
                    }
                    className={cn(
                      'flex flex-col items-center rounded-lg py-1.5 text-[11px] leading-tight transition-colors',
                      historical
                        ? heatClassHistory(cell.count)
                        : heatClass(cell.count),
                      cell.hasAny && 'hover:ring-1 hover:ring-primary/50',
                      !cell.hasAny && 'cursor-default',
                      cell.isToday && 'ring-1 ring-primary',
                    )}
                  >
                    <span className="font-medium">{cell.dayNum}</span>
                  </button>
                ),
              )}
            </div>
          </div>
        ) : null}

        {/* Type filters */}
        {!isLoading && !isError && summary ? (
          <div className="flex items-center gap-1.5 border-b border-border px-5 py-2.5">
            {typeChips.map(
              ({ key, types, label, count, icon: FilterIcon, on }) => {
                const active = types.some((t) => !hiddenTypes.has(t));
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => toggleTypes(types)}
                    title={
                      active
                        ? `Hide ${label.toLowerCase()}`
                        : `Show ${label.toLowerCase()}`
                    }
                    className={cn(
                      'flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                      active
                        ? on
                        : 'border-border bg-card text-muted-foreground/60 hover:text-foreground',
                    )}
                  >
                    <FilterIcon className="h-3.5 w-3.5" />
                    {label}
                    <span className="tabular-nums opacity-70">{count}</span>
                  </button>
                );
              },
            )}
          </div>
        ) : null}

        {/* Agenda */}
        <div
          className={cn(
            'flex-1 overflow-y-auto pb-4 transition-opacity duration-150',
            isFetching && !isLoading && 'opacity-50',
          )}
        >
          {isLoading ? (
            <SkeletonRows />
          ) : isError ? (
            <div className="flex flex-col items-center gap-3 px-5 py-16 text-center">
              <AlertTriangle className="h-6 w-6 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                Could not load the schedule.
              </span>
            </div>
          ) : visibleDays.length === 0 ? (
            <div className="flex flex-col items-center gap-3 px-5 py-16 text-center">
              {historical ? (
                <History className="h-8 w-8 text-muted-foreground/40" />
              ) : (
                <CalendarClock className="h-8 w-8 text-muted-foreground/40" />
              )}
              <div>
                <div className="text-sm font-medium text-foreground">
                  {days.length === 0
                    ? historical
                      ? 'Nothing happened'
                      : 'Nothing scheduled'
                    : 'Nothing to show'}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {days.length === 0
                    ? historical
                      ? 'No sends, charges, or payments in this window.'
                      : `No reminders, plan offers, or charges in the next ${data?.horizonDays || 30} days.`
                    : 'Everything is hidden by the filters above.'}
                </div>
              </div>
              {days.length > 0 ? (
                <button
                  type="button"
                  onClick={() => {
                    setHiddenTypes(new Set());
                    setFocusUser(null);
                  }}
                  className="flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-accent"
                >
                  <FilterX className="h-3.5 w-3.5" />
                  Clear filters
                </button>
              ) : null}
            </div>
          ) : (
            visibleDays.map((day) => (
              <div
                key={day.date}
                ref={(el) => {
                  dayRefs.current[day.date] = el;
                }}
                className="scroll-mt-2"
              >
                <div className="sticky top-0 z-10 flex items-baseline justify-between border-b border-border/60 bg-background/95 px-5 py-2 backdrop-blur">
                  <span
                    className={cn(
                      'text-xs font-semibold uppercase tracking-wide',
                      dayTitle(day.date) === 'Today'
                        ? 'text-primary'
                        : historical
                          ? 'text-stone-500'
                          : 'text-muted-foreground',
                    )}
                  >
                    {dayTitle(day.date)}
                  </span>
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {day.count > 0
                      ? `${day.count} event${day.count === 1 ? '' : 's'} · ${money(day.totalAmount)}`
                      : 'on hold'}
                  </span>
                </div>
                <div className="space-y-0.5 px-3 py-2">
                  {day.events.map((event, i) => {
                    const styleMap = event.historical
                      ? EVENT_STYLES_HISTORY
                      : EVENT_STYLES;
                    const style =
                      styleMap[event.type] ||
                      EVENT_STYLES[event.type] ||
                      EVENT_STYLES.reminder;
                    const failedCharge =
                      event.historical &&
                      event.type === 'installment' &&
                      event.meta?.outcome === 'failed';
                    const Icon = event.muted ? BellOff : style.icon;
                    const reason =
                      MUTED_REASON_COPY[event.meta?.mutedReason] || 'held';
                    return (
                      <div
                        key={`${event.type}-${event.invoiceId || event.claimId}-${i}`}
                        role="button"
                        tabIndex={0}
                        onClick={() => toggleFocus(event)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleFocus(event);
                          }
                        }}
                        title={
                          focusUser
                            ? 'Clear the person filter'
                            : `Show only ${event.client || 'this client'}`
                        }
                        className={cn(
                          'flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-accent/60',
                          event.muted && 'opacity-60',
                        )}
                      >
                        <div
                          className={cn(
                            'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
                            event.muted
                              ? 'bg-muted text-muted-foreground'
                              : failedCharge
                                ? 'bg-red-500/10 text-red-600 dark:text-red-400'
                                : event.meta?.retry
                                  ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
                                  : style.chip,
                          )}
                          title={
                            event.muted
                              ? `Held (${reason}), nothing will ${event.type === 'installment' ? 'charge' : 'send'}`
                              : failedCharge
                                ? 'This installment charge failed'
                                : event.meta?.retry
                                  ? 'Failed installment, retried daily until it clears'
                                  : style.label
                          }
                        >
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium text-foreground">
                            {event.client || 'Unknown client'}
                            {event.meta?.retry ? (
                              <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                                retrying daily
                              </span>
                            ) : null}
                          </div>
                          <div className="truncate text-xs text-muted-foreground">
                            {eventDetail(event)}
                            {event.muted ? ` · held (${reason})` : ''}
                          </div>
                          {event.historical && EMAIL_TYPES.has(event.type) ? (
                            <div className="mt-0.5">
                              <EmailOutcome meta={event.meta} />
                            </div>
                          ) : null}
                        </div>
                        <div className="shrink-0 text-right">
                          <div
                            className={cn(
                              'text-sm font-medium tabular-nums',
                              // History money outcome lives in the amount's
                              // color: green = collected, red = failed.
                              event.muted
                                ? 'text-muted-foreground'
                                : event.historical &&
                                    (event.type === 'payment' ||
                                      (event.type === 'installment' &&
                                        event.meta?.outcome === 'paid'))
                                  ? 'text-emerald-600 dark:text-emerald-400'
                                  : failedCharge
                                    ? 'text-red-600 dark:text-red-400'
                                    : 'text-foreground',
                            )}
                          >
                            {money(event.amount)}
                          </div>
                          {failedCharge ? (
                            <div className="text-[11px] font-medium text-red-600 dark:text-red-400">
                              failed
                            </div>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        {historical && summary ? (
          <div className="border-t border-border px-5 py-3">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <BadgeCheck className="h-3 w-3 shrink-0 text-emerald-600" />
              {money(summary.collectedAmount || 0)} collected in this window.
            </div>
          </div>
        ) : summary?.held > 0 ? (
          <div className="border-t border-border px-5 py-3">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <BellOff className="h-3 w-3 shrink-0" />
              {summary.held} held (greyed): nothing sends or charges.
            </div>
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
};

export default BillingSchedulePanel;
